// src/App.js
import React from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Import pages
import Home from './pages/Home';
import SurahPage from './pages/SurahPage';
import JuzPage from './pages/JuzPage';
import QuranPage from './pages/QuranPage';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Unauthorized from './pages/Auth/Unauthorized';

// Import Auth Components
import { ProtectedRoute, AdminRoute, ExpertRoute, UserRoute, SuperExpertRoute } from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import HomeAdmin from './pages/Admin/Home';
import Dashboard from './pages/Dashboard';
import ModelsPage from './pages/Models';
import ModelDetail from './pages/ModelDetail';
import ModelUpload from './pages/ModelUpload';
import ModelEdit from './pages/ModelEdit';
import Profile from './pages/Profile';

import ContributePage from './pages/ContributePage';
import ResourcesPage from './pages/Resources';
import ResourceDetail from './pages/ResourceDetail';

// Experts
import HomeExpert from './pages/Experts/Home';
import AyahVerificationPage from './pages/Experts/AyahVerification';
import TranslationVerificationPage from './pages/Experts/TranslationVerification';
import AudioVerificationPage from './pages/Experts/AudioVerification';

import SuperDashboard from './pages/Super/Home';
import SuperAyahVerificationPage from './pages/Super/AyahVerification';
import SuperTranslationVerificationPage from './pages/Super/TranslationVerification';
import SuperAudioVerificationPage from './pages/Super/AudioVerification';
import UserManagement from './pages/Admin/User';
import About from './pages/About';
import AyahVerificationReportPage from './pages/Super/Report/TextualReport';
import TranslationReportPage from './pages/Super/Report/TranslationReport';

// Customize theme
const theme = extendTheme({
  colors: {
    brand: {
      50: '#e6fffa',
      100: '#b2f5ea',
      500: '#319795',
      600: '#2C7A7B',
      700: '#285E61',
      900: '#1D4044',
    },
  },
  fonts: {
    heading: 'Roboto, sans-serif',
    body: 'Roboto, sans-serif',
  },
});

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            
            <Route path="/surahs" element={<SurahPage />} />
            <Route path="/surahs/:surahId" element={<SurahPage />} />
            <Route path="/juz" element={<JuzPage />} />
            <Route path="/juz/:juzId" element={<JuzPage />} />
            <Route path="/pages" element={<Navigate to="/pages/1" replace />} />
            <Route path="/pages/:page" element={<QuranPage />} />
            {/* Recitations now play inside the surah reader; Tafseer was removed */}
            <Route path="/media" element={<Navigate to="/surahs" replace />} />
            <Route path="/tafseer" element={<Navigate to="/surahs" replace />} />
            {/* Anyone can browse approved models; running demos and uploading need an account */}
            <Route path="/models" element={<ModelsPage />} />
            <Route path="/models/:id" element={<ModelDetail />} />
            {/* Visitors get a limited dashboard linking to everything that is open to them */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/about" element={<About />} />
            {/* Anyone can browse published resources; sharing and endorsing need an account */}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:id" element={<ResourceDetail />} />
            {/* Files and links became resources; keep old bookmarks working */}
            <Route path="/datasets" element={<Navigate to="/resources" replace />} />
            <Route path="/link-submission" element={<Navigate to="/resources?view=mine" replace />} />
            <Route path="/file-submission" element={<Navigate to="/resources?view=mine" replace />} />
            <Route path="/expert/link-verification" element={<Navigate to="/resources?view=review" replace />} />
            <Route path="/expert/file-verification" element={<Navigate to="/resources?view=review" replace />} />
            <Route path="/verifier/link-verification" element={<Navigate to="/resources?view=review" replace />} />
            <Route path="/verifier/file-verification" element={<Navigate to="/resources?view=review" replace />} />

            {/* Protected Routes - User level access */}
            <Route element={<UserRoute />}>
                {/* For uploading */}
              <Route path="/models/upload" element={<ModelUpload />} />
              <Route path="/models/:id/edit" element={<ModelEdit />} />
              <Route path="/contribute" element={<ContributePage />} />
              <Route path="/create" element={<ContributePage />} />
              <Route path="/profile" element={<Profile />} />

            </Route>
            
            {/* Expert level routes */}
            <Route element={<ExpertRoute />}>
              <Route path="/expert" element={<HomeExpert />} />
              <Route path="/expert/ayah-verification" element={<AyahVerificationPage />} />
              <Route path="/expert/translation-verification" element={<TranslationVerificationPage />} />
              <Route path="/expert/audio-verification" element={<AudioVerificationPage />} />
            </Route>

            <Route element={<SuperExpertRoute />}>
              <Route path="/verifier" element={<SuperDashboard />} />
              <Route path="/verifier/ayah-verification" element={<SuperAyahVerificationPage />} />
              <Route path="/verifier/translation-verification" element={<SuperTranslationVerificationPage />} />
              <Route path="/verifier/audio-verification" element={<SuperAudioVerificationPage />} />

              <Route path="/report/ayah" element={<AyahVerificationReportPage />} />
              <Route path="/report/translation" element={<TranslationReportPage />} />
            </Route>
            
            {/* Admin level routes */}
            <Route element={<AdminRoute />}>
              <Route path="/admin-dashboard" element={<HomeAdmin />} />
              <Route path="/admin/user" element={<UserManagement />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;